// src/components/practice/PracticeReportCard.tsx
"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { renderBody } from "@/lib/renderBody";
import Link from "next/link";
import { getEffectivePlan } from "@/lib/planMap";

type PracticeReport = {
  id: string;
  uid: string;
  displayName?: string | null;
  title?: string;
  body?: string;
  tool?: string | null;
  topicText?: string | null;
  imageUrls?: string[];
  createdAtLabel?: string;
  views?: number;
  likesCount?: number;
  isMine?: boolean;
  isPublic?: boolean;
  visibility?: "private" | "room" | "submit" | "public";
  plannedDate?: string | null;
};

type Props = {
  report: PracticeReport;
  showPublishToggle?: boolean;
  onDeleted?: () => void;
  truncate?: boolean; // true のとき本文を3行に切り詰めて「続きを読む →」を表示
};

const INTENT_LABELS: Record<string, string> = {
  talk: "話したい",
  play: "試したい",
  learn: "知りたい",
};

export default function PracticeReportCard({ report, showPublishToggle = false, onDeleted, truncate = false }: Props) {
  const router = useRouter();
  const [views, setViews] = useState<number>(report.views ?? 0);
  const [likesCount, setLikesCount] = useState<number>(report.likesCount ?? 0);
  const [likeLoading, setLikeLoading] = useState(false);
  const [liked, setLiked] = useState(false);
  const [isPublic, setIsPublic] = useState<boolean>(report.isPublic ?? false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [deleteMode, setDeleteMode] = useState<"idle" | "confirm" | "done">("idle");
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Response chain count
  const [responseCount, setResponseCount] = useState<number>(0);

  // Follow-up write choice (public card)
  const [followUpOpen, setFollowUpOpen] = useState(false);

  // Interest state
  const [interestTotal, setInterestTotal] = useState<number | null>(null);
  const [interestBreakdown, setInterestBreakdown] = useState<{ talk: number; play: number; learn: number } | null>(null);
  const [interestMode, setInterestMode] = useState<"idle" | "picking" | "done">("idle");
  const [interestLoading, setInterestLoading] = useState(false);
  const [myIntent, setMyIntent] = useState<string | null>(null);

  // Self-plan state (public card "やってみる" flow)
  const [selfPlanMode, setSelfPlanMode] = useState<"idle" | "picking" | "set">("idle");
  const [selfPlannedDate, setSelfPlannedDate] = useState("");

  // Date planning state
  const [userPlan, setUserPlan] = useState<string>("free");
  const [responseType, setResponseType] = useState<null | "write" | "event">(null);
  const [datePlanMode, setDatePlanMode] = useState<"idle" | "picking" | "set">(
    report.plannedDate ? "set" : "idle"
  );
  const [plannedDate, setPlannedDate] = useState(report.plannedDate ?? "");

  const viewKey = useMemo(
    () => `practice:viewed:${report.id}`,
    [report.id]
  );

  useEffect(() => {
    fetch("/api/user-profile")
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d === "object") setUserPlan(getEffectivePlan(d) ?? "free");
      })
      .catch(() => {});
  }, []);

  // 初期 liked 状態を取得
  useEffect(() => {
    fetch(`/api/practice/${report.id}/like`)
      .then((r) => r.json())
      .then((d) => { if (d?.liked) setLiked(true); })
      .catch(() => {});
  }, [report.id]);

  useEffect(() => {
    fetch(`/api/report-interests?reportId=${encodeURIComponent(report.id)}`)
      .then((r) => r.json())
      .then((d) => {
        setInterestTotal(d?.total ?? 0);
        if (d?.breakdown) setInterestBreakdown(d.breakdown);
        if (!report.isMine && d?.myIntent) {
          setMyIntent(d.myIntent);
          setInterestMode("done");
        }
      })
      .catch(() => setInterestTotal(0));
  }, [report.id, report.isMine]);

  useEffect(() => {
    if (!report.isMine) return;
    fetch(`/api/report-responses/count?reportId=${encodeURIComponent(report.id)}`)
      .then((r) => r.json())
      .then((d) => { if (typeof d?.count === "number") setResponseCount(d.count); })
      .catch(() => {});
  }, [report.id, report.isMine]);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(viewKey) === "1") return;
      sessionStorage.setItem(viewKey, "1");
    } catch {
      // noop
    }

    let cancelled = false;

    const run = async () => {
      try {
        const res = await fetch(`/api/practice/${report.id}/view`, {
          method: "POST",
        });
        if (!res.ok || cancelled) return;
        setViews((v) => v + 1);
      } catch {
        // noop
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [report.id, viewKey]);

  const handleTogglePublic = async () => {
    setPublishLoading(true);
    const next = !isPublic;
    const res = await fetch("/api/practice", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: report.id, isPublic: next }),
    }).catch(() => null);
    if (res?.ok) {
      const data = await res.json().catch(() => ({}));
      setIsPublic(next);
      // DISCOVERY30 付与があった場合はウェルカムページへ
      if (data?.discovery30 === "granted") {
        router.push("/welcome?ref=discovery30_granted");
        return;
      }
    }
    setPublishLoading(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await fetch("/api/practice", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: report.id }),
      });
      if (res.ok) {
        setDeleteMode("done");
        onDeleted?.();
      } else {
        const d = await res.json().catch(() => null);
        alert(d?.error ?? "削除に失敗しました");
        setDeleteMode("idle");
      }
    } catch {
      alert("通信エラー");
      setDeleteMode("idle");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLike = async () => {
    if (likeLoading) return;
    setLikeLoading(true);

    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikesCount((v) => Math.max(0, wasLiked ? v - 1 : v + 1));

    try {
      const res = await fetch(`/api/practice/${report.id}/like`, {
        method: wasLiked ? "DELETE" : "POST",
      });
      if (!res.ok) {
        setLiked(wasLiked);
        setLikesCount((v) => Math.max(0, wasLiked ? v + 1 : v - 1));
      }
    } catch {
      setLiked(wasLiked);
      setLikesCount((v) => Math.max(0, wasLiked ? v + 1 : v - 1));
    }

    setLikeLoading(false);
  };

  const handleSelectIntent = async (intent: string) => {
    if (interestLoading) return;
    setInterestLoading(true);
    const res = await fetch("/api/report-interests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reportId: report.id, intent }),
    }).catch(() => null);
    if (res?.ok) {
      const data = await res.json().catch(() => null);
      setMyIntent(intent);
      setInterestMode("done");
      if (typeof data?.total === "number") setInterestTotal(data.total);
    }
    setInterestLoading(false);
  };

  // Date planning helpers (computed per render, cheap)
  const formatYmd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  const jst = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Tokyo" }));
  const todayStr = formatYmd(jst);
  const maxDateStr =
    userPlan === "discovery_pro" || userPlan === "facilitator" || userPlan === "partner"
      ? formatYmd(new Date(jst.getFullYear(), jst.getMonth() + 2, 0))
      : userPlan === "discovery"
      ? formatYmd(new Date(jst.getFullYear(), jst.getMonth() + 1, 0))
      : todayStr;
  // Self-plan flow uses a wider range for free users
  const selfMaxDate = new Date(jst);
  if (userPlan === "discovery_pro" || userPlan === "facilitator" || userPlan === "partner") {
    selfMaxDate.setMonth(jst.getMonth() + 2);
    selfMaxDate.setDate(0);
  } else if (userPlan === "discovery") {
    selfMaxDate.setMonth(jst.getMonth() + 1);
    selfMaxDate.setDate(0);
  } else {
    selfMaxDate.setDate(jst.getDate() + 7);
  }
  const selfMaxDateStr = formatYmd(selfMaxDate);
  const formatDateJa = (iso: string) => {
    const [, m, d] = iso.split("-");
    return `${Number(m)}月${Number(d)}日`;
  };

  return (
    <article
      className={`rounded-xl border p-5 shadow-sm ${
        report.isMine
          ? "border-purple-300 bg-purple-50"
          : "border-gray-200 bg-white"
      }`}
    >
      {report.isMine && (
        <div className="mb-3 rounded-lg border border-purple-200 bg-white/70 px-3 py-2">
          <div className="text-xs font-semibold text-purple-700">あなたのレポート</div>
          <div className="mt-1 text-xs text-purple-900">
            {(() => {
              const reactionCount = likesCount + (interestTotal ?? 0) + responseCount;
              return reactionCount > 0
                ? `${views}人が読みました・${reactionCount}件の反応`
                : `${views}人が読みました`;
            })()}
          </div>
        </div>
      )}

      {report.isMine && interestTotal !== null && interestTotal > 0 && (
        <div className="mb-3">
          {datePlanMode === "idle" && responseType === null && (
            <div>
              <div className="text-xs text-gray-500 mb-1">どう応える？</div>
              <div className="flex gap-2">
                <button
                  onClick={() => setResponseType("write")}
                  className="text-xs px-3 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
                >
                  書いて応える
                </button>
                <button
                  onClick={() => setResponseType("event")}
                  className="text-xs px-3 py-1 rounded border border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  場をつくる
                </button>
              </div>
            </div>
          )}
          {datePlanMode === "idle" && responseType === "write" && (
            <div>
              <div className="text-xs text-gray-500 mb-1">どう書いて応える？</div>
              <div className="flex gap-2">
                <Link
                  href={`/practice/new?source=${encodeURIComponent(report.id)}&mode=respond`}
                  className="text-xs px-3 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
                >
                  実践レポートを書く
                </Link>
                <Link
                  href={`/discoveries?source=${encodeURIComponent(report.id)}&mode=respond`}
                  className="text-xs px-3 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
                >
                  発見ノートを書く
                </Link>
              </div>
              <button
                onClick={() => setResponseType(null)}
                className="mt-1 text-xs text-gray-400 hover:text-gray-600"
              >
                戻る
              </button>
            </div>
          )}

          {datePlanMode === "idle" && responseType === "event" && (
            <div>
              <button
                onClick={() => setDatePlanMode("picking")}
                className="text-xs px-3 py-1 rounded border border-purple-200 text-purple-700 hover:bg-purple-50"
              >
                関心に応えられそうな日を挙げる
              </button>
              <div className="mt-1 text-xs text-gray-400">
                話を聞いてみたいと思っている人に、予定を伝えられます
              </div>
            </div>
          )}
          {datePlanMode === "picking" && (
            <div className="rounded border border-purple-200 bg-white px-3 py-2 text-xs space-y-2">
              <div className="font-medium text-gray-700">応えられそうな日は？</div>
              <div className="text-gray-400">
                話を聞いてみたいと思っている人にこの予定を伝えて、参加のきっかけをつくります
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={plannedDate}
                  min={todayStr}
                  max={maxDateStr}
                  onChange={(e) => setPlannedDate(e.target.value)}
                  className="border rounded px-2 py-1 text-xs"
                />
                <button
                  onClick={() => {
                    if (!plannedDate) return;
                    setDatePlanMode("set");
                    console.log("[date-planned]", { reportId: report.id, plannedDate });
                    // Fire-and-forget: persist plannedDate on the report
                    fetch("/api/practice", {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ id: report.id, plannedDate }),
                    }).catch(() => {});
                    // Fire-and-forget: notify interested users
                    fetch("/api/notifications/create", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        reportId: report.id,
                        plannedDate,
                        authorName: report.displayName ?? "",
                      }),
                    }).catch(() => {});
                  }}
                  disabled={!plannedDate}
                  className="px-3 py-1 rounded bg-purple-600 text-white text-xs disabled:opacity-40"
                >
                  この日を伝える
                </button>
                <button
                  onClick={() => { setDatePlanMode("idle"); setResponseType(null); }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  戻る
                </button>
              </div>
              <div className="text-gray-500">
                {userPlan === "free"
                  ? "無料プランでは今日のみ選べます"
                  : userPlan === "discovery"
                  ? "今月内の日付を選べます"
                  : "来月までの日付を選べます"}
              </div>
            </div>
          )}
          {datePlanMode === "set" && plannedDate && (
            <div className="text-xs text-purple-700">
              {formatDateJa(plannedDate)}なら応えられそうだと伝えました
            </div>
          )}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        {report.isMine && !report.displayName ? (
          <Link
            href="/account?tab=basic"
            className="font-medium text-amber-600 underline hover:text-amber-800"
          >
            名前未設定（設定する →）
          </Link>
        ) : (
          <span className="font-medium text-gray-700">
            {report.displayName || "名前未設定"}
          </span>
        )}

        {report.tool ? (
          <span className="rounded bg-gray-100 px-2 py-0.5 text-gray-700">
            {report.tool}
          </span>
        ) : null}

        {report.createdAtLabel ? <span>{report.createdAtLabel}</span> : null}

        {!report.isMine && <span>閲覧 {views}</span>}
        {!report.isMine && <span>いいね {likesCount}</span>}
      </div>

      {report.title ? (
        <h2 className="mb-3 text-lg font-semibold leading-7 text-gray-900">
          {report.title}
        </h2>
      ) : null}

      {report.topicText ? (
        <div className="mb-3 text-sm text-gray-600">
          <span className="font-medium">関連するお題：</span>
          {report.topicText}
        </div>
      ) : null}

      {report.body ? (
        truncate ? (
          <div>
            <p className="text-sm leading-6 text-gray-700 line-clamp-3">
              {report.body.replace(/\*\*/g, "").replace(/\*/g, "")}
            </p>
            <Link
              href={`/practice/${report.id}`}
              className="mt-1 inline-block text-xs text-violet-600 hover:text-violet-800"
            >
              続きを読む →
            </Link>
          </div>
        ) : (
          <div className="text-sm leading-7 text-gray-800">
            {renderBody(report.body)}
          </div>
        )
      ) : null}

      {report.imageUrls && report.imageUrls.length > 0 && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {report.imageUrls.map((url, idx) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              key={idx}
              src={url}
              alt={`写真 ${idx + 1}`}
              className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => window.open(url, "_blank")}
            />
          ))}
        </div>
      )}

      {!report.isMine && report.plannedDate && (
        <div className="text-xs text-purple-700 mt-2">
          {formatDateJa(report.plannedDate)}に場をつくる予定です
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        {!report.isMine && (
          <button
            onClick={handleLike}
            disabled={likeLoading}
            className={`text-xs flex items-center gap-1 disabled:opacity-40 ${
              liked ? "text-rose-500" : "text-gray-400 hover:text-rose-400"
            }`}
          >
            {liked ? "♥" : "♡"}
            {likesCount > 0 && <span>{likesCount}</span>}
          </button>
        )}
        {showPublishToggle && report.visibility === "submit" ? (
          <span className="text-xs px-2 py-1 rounded border border-orange-200 bg-orange-50 text-orange-600">
            提出済み
          </span>
        ) : showPublishToggle && report.visibility === "room" ? (
          <>
            <span className="text-xs px-2 py-1 rounded border border-blue-200 bg-blue-50 text-blue-600">
              ルーム内公開
            </span>
            <button
              onClick={handleTogglePublic}
              disabled={publishLoading}
              className="text-xs px-3 py-1 rounded border border-violet-400 bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-40"
            >
              全体公開にする
            </button>
          </>
        ) : showPublishToggle ? (
          <button
            onClick={handleTogglePublic}
            disabled={publishLoading}
            className={`text-xs px-3 py-1 rounded border disabled:opacity-40 ${
              isPublic
                ? "border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
                : "border-violet-400 bg-violet-50 text-violet-700 hover:bg-violet-100"
            }`}
          >
            {isPublic ? "公開中" : "公開する"}
          </button>
        ) : null}
        {showPublishToggle && report.visibility !== "submit" && deleteMode === "idle" && (
          <Link
            href={`/practice/new?edit=${report.id}`}
            className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors"
          >
            編集
          </Link>
        )}
        {showPublishToggle && deleteMode === "idle" && (
          <button
            onClick={() => setDeleteMode("confirm")}
            className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-400 hover:border-red-300 hover:text-red-400 transition-colors"
          >
            削除
          </button>
        )}
        {showPublishToggle && deleteMode === "confirm" && (
          <span className="flex items-center gap-2">
            <span className="text-xs text-gray-500">本当に削除しますか？</span>
            <button
              onClick={handleDelete}
              disabled={deleteLoading}
              className="text-xs px-2 py-1 rounded border border-red-300 text-red-500 hover:bg-red-50 disabled:opacity-40 transition-colors"
            >
              {deleteLoading ? "…" : "削除する"}
            </button>
            <button
              onClick={() => setDeleteMode("idle")}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              キャンセル
            </button>
          </span>
        )}
      </div>

      {!report.isMine && (
        <div className="mt-3">
          {interestTotal !== null && interestTotal > 0 && (
            <div className="text-xs text-gray-400 mb-1">{interestTotal}人が関心を示しています</div>
          )}
          {interestMode === "idle" && (
            <button
              onClick={() => setInterestMode("picking")}
              className="text-xs px-3 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
            >
              話を聞いてみたい
            </button>
          )}
          {interestMode === "picking" && (
            <div className="flex gap-2">
              {Object.entries(INTENT_LABELS).map(([intent, label]) => (
                <button
                  key={intent}
                  onClick={() => handleSelectIntent(intent)}
                  disabled={interestLoading}
                  className="text-xs px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-40"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {!followUpOpen && selfPlanMode === "idle" && (
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => setFollowUpOpen(true)}
                className="text-xs px-3 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
              >
                やってみた
              </button>
              <button
                onClick={() => setSelfPlanMode("picking")}
                className="text-xs px-3 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
              >
                やってみる
              </button>
            </div>
          )}

          <div className="mt-3">
            {followUpOpen && (
              <div>
                <div className="text-xs text-gray-500 mb-1">どう書く？</div>
                <div className="flex gap-2">
                  <Link
                    href={`/practice/new?source=${encodeURIComponent(report.id)}&mode=respond`}
                    className="text-xs px-3 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
                  >
                    実践レポートを書く
                  </Link>
                  <Link
                    href={`/discoveries?source=${encodeURIComponent(report.id)}&mode=respond`}
                    className="text-xs px-3 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
                  >
                    発見ノートを書く
                  </Link>
                </div>
                <button
                  onClick={() => setFollowUpOpen(false)}
                  className="mt-1 text-xs text-gray-400 hover:text-gray-600"
                >
                  戻る
                </button>
              </div>
            )}
          </div>

          <div className="mt-3">
            {selfPlanMode === "picking" && (
              <div className="rounded border border-gray-200 bg-white px-3 py-2 text-xs space-y-2">
                <div className="text-gray-400">この予定は関心のある人に伝わります</div>
                <div className="font-medium text-gray-700">できそうな日は？</div>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={selfPlannedDate}
                    min={todayStr}
                    max={selfMaxDateStr}
                    onChange={(e) => setSelfPlannedDate(e.target.value)}
                    className="border rounded px-2 py-1 text-xs"
                  />
                  <button
                    onClick={() => {
                      if (!selfPlannedDate) return;
                      setSelfPlanMode("set");
                      fetch("/api/practice-plans", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          sourceReportId: report.id,
                          plannedDate: selfPlannedDate,
                        }),
                      }).catch(() => {});
                    }}
                    disabled={!selfPlannedDate}
                    className="px-3 py-1 rounded bg-gray-700 text-white text-xs disabled:opacity-40"
                  >
                    この日にやってみる
                  </button>
                  <button
                    onClick={() => setSelfPlanMode("idle")}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    戻る
                  </button>
                </div>
                <div className="text-gray-500">
                  {userPlan === "free"
                    ? "1週間先まで選べます"
                    : userPlan === "discovery"
                    ? "今月内で選べます"
                    : "翌月まで選べます"}
                </div>
              </div>
            )}
            {selfPlanMode === "set" && selfPlannedDate && (
              <div className="text-xs text-gray-600">
                {formatDateJa(selfPlannedDate)}にやってみます
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
